import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContextType from "../../../../domain/providers/localization/LocalizationContextType";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import di from "../../../../dependency_injection";
import QAResumeEntity from "../../../../domain/entities/qa_resume.entity";
import SearchResultEntity from "../../../../domain/entities/search_result.entity";
import GetAudioResultUseCase, { GetAudioResultUseCaseName } from "../../../../domain/use_cases/qa_resume/get_audio_result.use_case";
import SearchQAResumesUseCase, { SearchQAResumesUseCaseName } from "../../../../domain/use_cases/qa_resume/search_qa_resumes.use_case";
import LoadingComponent from "../../../components/LoadingComponent/loading.component";
import NotFoundComponent from "../../../components/notFound/not_found.component";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import routes from "../../../routes/Routes";
import ResultIAAudioComponent from "../../ai_agent/detailed/components/result_question/result_ia_audio.component";
import SearchHumanAgentsUseCase, { SearchHumanAgentsUseCaseName } from "../../../../domain/use_cases/human_agent/search_human_agents.use_case";
import { AutoCompleteItem } from "../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import HumanAgentEntity from "../../../../domain/entities/human_agent.entity";
import GetHumanAgentByIdUseCase, { GetHumanAgentByIdUseCaseName } from "../../../../domain/use_cases/human_agent/get_human_agent_by_id.use_case";
import DownloadAllQAResumeUseCase, { DownloadAllQAResumeUseCaseName } from "../../../../domain/use_cases/qa_resume/download_all_qa_resume.use_case";

const useQAResumeListViewModel = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchQuery = searchParams.get("search");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");
    const humanAgentQuery = searchParams.get("human_agent_id");
    const humanAgentNameQuery = searchParams.get("human_agent_name");
    const fromQuery = searchParams.get("from");
    const toQuery = searchParams.get("to");
    const audioNameQuery = searchParams.get("audio_name");
    const callStateQuery = searchParams.get("call_state");
    const sentimentQuery = searchParams.get("sentiment");

    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { pushModalCustom, popModalCustom, addToast, openModalCustom } = useContext(ModalsContext);
    const formInfo = useForm();
    const { register, handleSubmit, getValues, setValue, watch } = formInfo;
    const [result, setResult] = useState<SearchResultEntity<QAResumeEntity> | undefined>(undefined);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    const itemsPerPage = 20;
    let _timerTap: any;

    const _handleReload = async (jobId: string): Promise<void> => {
        _handleOpenVisibility(jobId);
    };

    const _handleOpenVisibility = async (jobId: string) => {
        pushModalCustom("sm", "", <LoadingComponent />, { hideOnTap: false });
        const response = await di.get<GetAudioResultUseCase>(GetAudioResultUseCaseName).call(jobId);
        popModalCustom();
        if (isLeft(response)) {
            addToast(i18n(response.left.code ?? KeyWordLocalization.AudioZoneComponentErrorGettingData), "error", undefined);
            pushModalCustom("sm", i18n(KeyWordLocalization.AudioZoneComponentResultTitle), <NotFoundComponent />);
            return;
        }
        pushModalCustom("lg", i18n(KeyWordLocalization.AudioZoneComponentResultTitle), <ResultIAAudioComponent result={response.right} reload={() => _handleReload(jobId)} />);
    };

    const handleShowModal = (resume: QAResumeEntity) => {
        openModalCustom(
            "lg",
            i18n(KeyWordLocalization.QAResumeListPageModalTitle),
            <div className="container qa_resume">
                <div className="row">
                    {/* <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityId)}</label>
                        <p>{resume.id}</p>
                    </div> */}
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityJobId)}</label>
                        <p>{resume.job_id}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityAudioName)}</label>
                        <p title={resume.audio_name}
                            style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}>{resume.audio_name}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityJobStatus)}</label>
                        <p>{resume.job_status}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityQaAgentName)}</label>
                        <p>{resume.qa_agent_name}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityOperationName)}</label>
                        <p>{resume.operation_name}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityClientName)}</label>
                        <p>{resume.client_name}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityHumanAgentName)}</label>
                        <p>{resume.human_agent_name}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityStartAt)}</label>
                        <p>{new Date(resume.start_at).toLocaleString()}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityEndAt)}</label>
                        <p>{new Date(resume.end_at).toLocaleString()}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityCreatedAt)}</label>
                        <p>{new Date(resume.created_at).toLocaleString()}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityAgentScore)}</label>
                        <p>{resume.agent_score.toFixed(1)}%</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityCustomerScore)}</label>
                        <p>{resume.customer_score.toFixed(1)}%</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityAverageScore)}</label>
                        <p>{resume.average_score.toFixed(1)}%</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityAgentSentiment)}</label>
                        <p>{resume.agent_sentiment}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityCustomerSentiment)}</label>
                        <p>{resume.customer_sentiment}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityCallType)}</label>
                        <p>{resume.call_type}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityTarget)}</label>
                        <p>{resume.target.toFixed(0)}</p>
                    </div>
                    <div className="col-6 col-md-4">
                        <label>{i18n(KeyWordLocalization.QAResumeEntityAchieved)}</label>
                        <p>{resume.achieved.toFixed(0)}</p>
                    </div>
                </div>
            </div>
        );
    };

    const _searchQAResumes = async () => {
        const data = getValues();
        clearTimeout(_timerTap);
        setResult(undefined);

        const callState = Array.isArray(data[KeyWordLocalization.QAResumeEntityJobStatus]) ? data[KeyWordLocalization.QAResumeEntityJobStatus][0]?.id : null;
        const sentiment = Array.isArray(data[KeyWordLocalization.QAResumeEntityCustomerSentiment]) ? data[KeyWordLocalization.QAResumeEntityCustomerSentiment][0]?.id : null;        
        const audioName = data["audio_name"] ?? null;
        const humanAgentId = Array.isArray(data[KeyWordLocalization.QAResumeListPageHumanAgent]) && data[KeyWordLocalization.QAResumeListPageHumanAgent].length > 0 
            ? data[KeyWordLocalization.QAResumeListPageHumanAgent][0]?.id 
            : (data[KeyWordLocalization.QAResumeListPageHumanAgentId] ?? null);
        const searchQuery = data[KeyWordLocalization.QAResumeListPageQuery] ?? null;
        const humanAgentName = Array.isArray(data[KeyWordLocalization.QAResumeListPageHumanAgent]) && data[KeyWordLocalization.QAResumeListPageHumanAgent].length > 0 
            ? data[KeyWordLocalization.QAResumeListPageHumanAgent][0]?.label 
            : null;
        const startDate = data[KeyWordLocalization.QAResumeListPageFrom] ?? null;
        const endDate = data[KeyWordLocalization.QAResumeListPageTo] ?? null;

        clearTimeout(_timerTap);
        setResult(undefined);

        const filters: any = {
            page_number: data[KeyWordLocalization.QAResumeListCurrentPage] || 1,
            page_size: itemsPerPage,
        };

        if (data[KeyWordLocalization.QAResumeListPageFrom]) {
            filters.start_date = data[KeyWordLocalization.QAResumeListPageFrom];
        }

        if (data[KeyWordLocalization.QAResumeListPageTo]) {
            filters.end_date = data[KeyWordLocalization.QAResumeListPageTo];
        }

        if (callState) {
            filters.call_state = callState;
        }

        if (sentiment) {
            filters.sentiment = sentiment;
        }

        if (audioName) {
            filters.audio_name = audioName;
        }

        if (humanAgentId) {
            filters.human_agent_id = humanAgentId;
        }

        if (humanAgentName) {
            filters.human_agent_name = humanAgentName;
        }

        if (searchQuery) {
            filters.query = searchQuery;
        }

        const response = await di
            .get<SearchQAResumesUseCase>(SearchQAResumesUseCaseName)
            .call(filters);


        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    // const _handleSearch = (data: any) => {
    //     const search = data[KeyWordLocalization.QAResumeListPageQuery];
    //     const page = data[KeyWordLocalization.QAResumeListCurrentPage];
    //     const humanAgentId = data[KeyWordLocalization.QAResumeListPageHumanAgentId];
    //     const from = data[KeyWordLocalization.QAResumeListPageFrom] || null; // Nuevo campo
    //     const to = data[KeyWordLocalization.QAResumeListPageTo] || null; // Nuevo campo

    //     let query = "?";
    //     if (search && search.length > 0) query += `search=${search}&`;
    //     if (page) query += `page=${page}&`;
    //     if (humanAgentId) query += `human_agent_id=${humanAgentId}&`;
    //     if (from) query += `from=${from}&`;
    //     if (to) query += `to=${to}&`;
    //     if (query.endsWith("&")) query = query.slice(0, -1);

    //     navigate(routes.qa_resumes_list.relativePath + query);
    // };
    const _handleSearch = (data: any) => {
        const search = data[KeyWordLocalization.QAResumeListPageQuery];
        const page = data[KeyWordLocalization.QAResumeListCurrentPage];
        const humanAgentId = Array.isArray(data[KeyWordLocalization.QAResumeListPageHumanAgent]) && data[KeyWordLocalization.QAResumeListPageHumanAgent].length > 0 
            ? data[KeyWordLocalization.QAResumeListPageHumanAgent][0]?.id 
            : (data[KeyWordLocalization.QAResumeListPageHumanAgentId] ?? null);
        const from = data[KeyWordLocalization.QAResumeListPageFrom] ?? null;
        const to = data[KeyWordLocalization.QAResumeListPageTo] ?? null;

        const callState = Array.isArray(data[KeyWordLocalization.QAResumeEntityJobStatus])
            ? data[KeyWordLocalization.QAResumeEntityJobStatus][0]?.id
            : null;

        const sentiment = Array.isArray(data[KeyWordLocalization.QAResumeEntityCustomerSentiment])
            ? data[KeyWordLocalization.QAResumeEntityCustomerSentiment][0]?.id
            : null;

        const audioName = data["audio_name"] ?? null;
        
        const humanAgentName = Array.isArray(data[KeyWordLocalization.QAResumeListPageHumanAgent]) && data[KeyWordLocalization.QAResumeListPageHumanAgent].length > 0 
            ? data[KeyWordLocalization.QAResumeListPageHumanAgent][0]?.label 
            : null;

        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if (page) query += `page=${page}&`;
        if (humanAgentId) query += `human_agent_id=${humanAgentId}&`;
        if (humanAgentName) query += `human_agent_name=${encodeURIComponent(humanAgentName)}&`;
        if (from) query += `from=${from}&`;
        if (to) query += `to=${to}&`;
        if (audioName) query += `audio_name=${audioName}&`;
        if (callState) query += `call_state=${callState}&`;
        if (sentiment) query += `sentiment=${sentiment}&`;

        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.qa_resumes_list.relativePath + query);
    };


    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleLoad = async () => {
        setValue(KeyWordLocalization.QAResumeListPageQuery, searchQuery);
        setValue(KeyWordLocalization.QAResumeListCurrentPage, pageQuery);
        setValue(KeyWordLocalization.QAResumeListPageHumanAgentId, humanAgentQuery);
        setValue(KeyWordLocalization.QAResumeListPageFrom, fromQuery);
        setValue(KeyWordLocalization.QAResumeListPageTo, toQuery);
        setValue("audio_name", audioNameQuery);
        
        // Load call state from URL
        if (callStateQuery) {
            setValue(KeyWordLocalization.QAResumeEntityJobStatus, [{
                id: callStateQuery,
                label: callStateQuery,
                aditionalValue: callStateQuery
            }]);
        }
        
        // Load sentiment from URL
        if (sentimentQuery) {
            setValue(KeyWordLocalization.QAResumeEntityCustomerSentiment, [{
                id: sentimentQuery,
                label: sentimentQuery,
                aditionalValue: sentimentQuery
            }]);
        }

        await _loadHumanAgent();
        
        // Wait a bit to ensure all setValue operations have completed
        setTimeout(() => {
            _searchQAResumes();
        }, 100);
    };

    const handleChangeHumanAgent = (item: AutoCompleteItem<HumanAgentEntity>) => {
        // Also set the human agent object for the AutoComplete component
        if (item) {
            setValue(KeyWordLocalization.QAResumeListPageHumanAgentId, item.id);
            setValue(KeyWordLocalization.QAResumeListPageHumanAgent, [item]);
        } else {
            // Clear both fields when deselecting
            setValue(KeyWordLocalization.QAResumeListPageHumanAgentId, null);
            setValue(KeyWordLocalization.QAResumeListPageHumanAgent, []);
        }
        
        // Use the same flow as other filters instead of calling _searchQAResumes directly
        handleSearchOnChange();
    };

    const _loadHumanAgent = async () => {
        if (humanAgentQuery && getValues()[KeyWordLocalization.QAResumeListPageHumanAgent] == "") {
            const response = await di.get<GetHumanAgentByIdUseCase>(GetHumanAgentByIdUseCaseName).call(humanAgentQuery);
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<HumanAgentEntity> = {
                label: response.right.name,
                id: response.right.id,
                aditionalValue: response.right,
            };
            setValue(KeyWordLocalization.QAResumeListPageHumanAgent, [parsed]);
        }
    }



    const _onSearchHuman = async (query: string): Promise<AutoCompleteItem<HumanAgentEntity>[]> => {
        const response = await di.get<SearchHumanAgentsUseCase>(SearchHumanAgentsUseCaseName).call({
            itemsPerPage: 20,
            page: 1,
            query,
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: item.name,
                id: item.id,
                aditionalValue: item,
            }
        });
        return parsed;
    };


    const _onSearchCallState = async (query: string): Promise<AutoCompleteItem<string>[]> => {
        const options = ["CREATED", "IN_PROGRESS", "COMPLETED", "NOT_RATED", "FAILED"];
        return options
            .filter(state => state.toLowerCase().includes(query.toLowerCase()))
            .map(state => ({
                label: state,
                id: state,
                aditionalValue: state,
            }));
    };

    const _onSearchSentiment = async (query: string): Promise<AutoCompleteItem<string>[]> => {
        const options = ["Positive", "Negative", "Neutral"];
        return options
            .filter(sentiment => sentiment.toLowerCase().includes(query.toLowerCase()))
            .map(sentiment => ({
                label: sentiment,
                id: sentiment,
                aditionalValue: sentiment,
            }));
    };



    const _handleOnChangePage = (newPage: number) => {
        const data = getValues();
        data[KeyWordLocalization.QAResumeListCurrentPage] = newPage;
        _handleSearch(data);
    };

    const handleDownloadData = async () => {
        setLoadingDownload(true);
        const data = getValues();

        const response = await di.get<DownloadAllQAResumeUseCase>(DownloadAllQAResumeUseCaseName).call({
            humanAgentId: data[KeyWordLocalization.QAResumeListPageHumanAgentId],
            from: data[KeyWordLocalization.QAResumeListPageFrom] || null, // Nuevo campo
            to: data[KeyWordLocalization.QAResumeListPageTo] || null, // Nuevo campo
        });

        setLoadingDownload(false);
        if (isLeft(response)) {
            addToast(i18n(response.left.code ?? KeyWordLocalization.QAResumeListPageErrorDownloadingAll), "error", undefined);
            return;
        }
        addToast(i18n(KeyWordLocalization.QAResumeListPageSuccessDownloadingAll), "success", undefined);

    };

    const handleOnChangeForm = () => {
        const data = getValues();
        _searchQAResumes();
        _handleSearch(data);
    }

    const onSubmit = (data: any) => {
        _handleSearch(data);
    };

    const handleSearchOnChange = () => {
        setTimeout(() => {
            const currentValues = getValues();
            // Instead of navigating, directly search and then update URL
            _searchQAResumes();
            _handleSearch(currentValues); // This will update the URL
        }, 0);
    };

    useEffect(() => {
        _handleLoad();
    }, [pageQuery, searchQuery, humanAgentQuery, humanAgentNameQuery, fromQuery, toQuery, audioNameQuery, callStateQuery, sentimentQuery]);

    return {
        formInfo,
        result,
        i18n,
        register,
        handleSubmit,
        _handleSearch,
        _handleChangeTextSearch,
        _handleOnChangePage,
        _onSearchHuman,
        _handleOpenVisibility,
        handleShowModal,
        handleDownloadData,
        loadingDownload,
        handleOnChangeForm,
        handleSearchOnChange,
        handleChangeHumanAgent,
        _onSearchCallState,
        _onSearchSentiment,
    };
};

export default useQAResumeListViewModel;